import { z } from 'zod';

export const TimelineErrorSchema = z
  .object({
    error: z.string().optional(),
    message: z.string().optional(),
    statusCode: z.number().optional(),
    code: z.string().optional(),
    retryAfter: z.number().nullable().optional(),
    details: z.array(z.unknown()).optional(),
  })
  .passthrough();

export type TimelineError = z.infer<typeof TimelineErrorSchema>;

type ErrorStatus = number;

// Build a typed map of status codes to the shared TimelineError schema.
const buildErrorResponseMap = (statuses: readonly ErrorStatus[]) => {
  const responseMap: Record<ErrorStatus, typeof TimelineErrorSchema> = {};
  for (const status of statuses) {
    responseMap[status] = TimelineErrorSchema;
  }
  return responseMap;
};

const AUTH_ERROR_STATUSES = [401, 403, 500] as const;
export const AUTH_ERROR_RESPONSES = buildErrorResponseMap(AUTH_ERROR_STATUSES);

const AUTH_BAD_REQUEST_STATUSES = [400, 401, 403, 500] as const;
export const AUTH_BAD_REQUEST_ERROR_RESPONSES = buildErrorResponseMap(AUTH_BAD_REQUEST_STATUSES);

const AUTH_BAD_REQUEST_RATE_LIMIT_STATUSES = [400, 401, 403, 429, 500] as const;
export const AUTH_BAD_REQUEST_RATE_LIMIT_ERROR_RESPONSES = buildErrorResponseMap(AUTH_BAD_REQUEST_RATE_LIMIT_STATUSES);

const AUTH_BAD_REQUEST_NOT_FOUND_STATUSES = [400, 401, 403, 404, 500] as const;
export const AUTH_BAD_REQUEST_NOT_FOUND_ERROR_RESPONSES = buildErrorResponseMap(AUTH_BAD_REQUEST_NOT_FOUND_STATUSES);

const AUTH_BAD_REQUEST_NOT_FOUND_RATE_LIMIT_STATUSES = [400, 401, 403, 404, 429, 500] as const;
export const AUTH_BAD_REQUEST_NOT_FOUND_RATE_LIMIT_ERROR_RESPONSES = buildErrorResponseMap(
  AUTH_BAD_REQUEST_NOT_FOUND_RATE_LIMIT_STATUSES
);

const AUTH_NOT_FOUND_STATUSES = [401, 403, 404, 500] as const;
export const AUTH_NOT_FOUND_ERROR_RESPONSES = buildErrorResponseMap(AUTH_NOT_FOUND_STATUSES);

const PUBLIC_BAD_REQUEST_STATUSES = [400, 500] as const;
export const PUBLIC_BAD_REQUEST_ERROR_RESPONSES = buildErrorResponseMap(PUBLIC_BAD_REQUEST_STATUSES);

// Convenience helper for ad-hoc status combinations alongside the canned exports.
export const createErrorResponseMap = (...statuses: ErrorStatus[]) => buildErrorResponseMap(statuses);
